// @refresh reset — dev-only: force remount so board nodes hot-reload on edit
import { useMemo } from "react";
import { type Node, type Edge, type NodeTypes } from "@xyflow/react";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox, SectionGrid } from "./SectionBox";
import { DocumentFrame } from "./DocumentFrame";
import { PresentationFrame } from "./PresentationFrame";
import { DesignerStepper, DesignerPlate, DESIGNER_SCREENS } from "./everything420/designer";
import { Everything420PricingSheet } from "./everything420/pricing";

// ─────────────────────────────────────────────────────────────────────────────
// THE EVERYTHING 420 BOARD — the engagement surface for a live PROSPECT.
//
// Everything 420 (everythingfor420.com) is a United States online smoke shop and
// cannabis-accessories retailer, trading eight years: direct-to-consumer plus a
// wholesale arm, roughly 4,070 live SKUs, warehoused in Dallas, on Shopify and
// moving to Medusa. Shuki Far on the business, Tomer Kom on the technical side,
// introduced by Matan Amoyal. We are competing to build them an AI operating
// system. Nothing is signed, and nothing on this board has been sent to them.
//
// ─────────────────────────────────────────────────────────────────────────────
// STANDARDISED 7 Aug 2026 (Erez authorised). SECOND board through the standard,
// after Air Duct. A board RENDERS OUR PROCESS — it is not a list that grew by
// accretion. Six sections, this fixed order, the same on every project board:
//
//   0 · THE PROJECT   — the project summary, presented. Always first.
//   1 · DISCOVERY     — who they are, what they want, their own documents.
//   2 · SCOPE         — the scoping attempt: today vs with us, their pains in
//                       their words, the root causes.
//   3 · COMMERCIAL    — scope and price. (The work order, when there is one.)
//   4 · THE SYSTEM    — Tailormind as built for THIS business. Carries a
//                       CHANGELOG and an ATLAS REVIEWED TO line at its top.
//   5 · LIVE          — what is actually running: film, flow, screens.
//
// The standard is kb-doc/myjarvis-os/architecture/tailormind-platform; ours is
// the INSTANCE, and the divergences are stated below rather than absorbed
// silently, because a justified divergence is how the standard improves.
//
// ⚠️ WHAT CAME OFF, AND WHERE IT WENT. The system DIAGRAM was removed on Erez's
// instruction — "the diagram is not good, remove it for now, we will come back
// to it." It was NOT deleted: every claim it carried (the connector-is-a-slot
// answer to the Medusa migration, pointers-not-copies, the knowledge layer their
// systems have no field for, mini-applications, the control plane) now lives in
// kb-doc/consultancy/everything-420/architecture, sections 01 to 06, and the
// code is still at board/everything420/system.tsx and still in git. Section 4
// presents that page. Same for the two card walls that used to sit in this file:
// the PLAN and the OPEN DECISIONS are now sections of the project page, which is
// where "where we stand" belongs — a board is never the home of prose.
//
// ⚠️ THREE DIVERGENCES FROM THE STANDARD, stated on purpose:
//
//   1. SECTION 5 IS NOT LIVE, AND CANNOT BE. Everything 420 is a prospect: no
//      box, no connector, nothing deployed. What section 5 holds is what we can
//      SHOW — the film, the clickable flow, the eight screens. The standard
//      should probably say that FIVE means "what is deployed" for a client and
//      "what exists to show" for a prospect; the section is the same shelf
//      either way, so this is a wording gap, not a structural one.
//   2. SECTION 3 CARRIES A BESPOKE INTERACTIVE COMPONENT — the feature/pricing
//      sheet — which is none of the three allowed section types. Kept
//      deliberately, exactly as Air Duct kept its calculator: Erez asked for a
//      live sheet rather than a link out to one. Flagged, not silently absorbed.
//   3. SECTION 4 HAS NO DIAGRAM, only the document. That is Erez's instruction
//      above, not a reading of the standard.
//
// ⚠️ THE ONE CORRECTION TO OUR OWN RECORD, kept here because it keeps resurfacing:
// there are TWO recorded calls, 83 (19 July, the richer one, filed under the
// wrong Vexa title "Billing Offers Team Discussion") and 96 (27 July). Meeting 92
// is a DIFFERENT prospect — a D2C launch in Brazil. Search transcript CONTENT,
// never meeting titles; the titles cost us two wrong conclusions.
//
// Owner agent: Ruby. Do not edit this file from another agent.
// ─────────────────────────────────────────────────────────────────────────────

const T = { bg: "#EFEFEF", dots: "#DCDCDC", ink: "#37352F", ink2: "#787774" };
const FONT = 'ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';

// ── EDGE-CACHE SALT ─────────────────────────────────────────────────────────
// Bump this string to mint a fresh entry-bundle hash. It is rendered into the
// DOM as a data attribute below, so neither tree-shaking nor minification can
// remove it — a COMMENT cannot do this job (a previous attempt added eight
// comment lines, the minifier stripped them, the bundle came out byte-identical
// and the site stayed down).
//
// WHY YOU WOULD NEED IT. Cloudflare can cache the SPA index.html fallback under
// a hashed /assets/*.js URL, which is served `immutable, max-age=31536000`. Every
// visitor then gets 1145 bytes of HTML where a module was expected and the app
// hangs on Loading forever. Vite names chunks by CONTENT hash, so redeploying
// the same code reuses the poisoned filename and cannot recover. Changing the
// emitted bytes is the only fix.
//
// ⚠️ AND THE RULE THAT MATTERS MORE, because the fix is what causes the fault.
// This happened to Outform on 6 Aug and I did the identical thing here on 7 Aug:
// a verification loop that reads index.html and fetches the new asset IN THE
// SAME ITERATION will request that asset before it has reached your edge, and
// Cloudflare will cache the fallback it gets — under a one-year immutable
// header. Your own verify curl poisons the bundle you just shipped.
// So: after the new hash appears in index.html, WAIT before fetching the asset,
// and verify against the deployment-specific *.pages.dev URL first — that one
// is not fronted by the poisoned cache entry.
const BUILD_SALT = "e420-2026-08-07-b";
// ────────────────────────────────────────────────────────────────────────────

/** The one caption style on this board: what the thing is, and why it is here. */
function Caption({ title, body, w = 1500 }: { title: string; body: string; w?: number }) {
  return (
    <div style={{ fontFamily: FONT, maxWidth: w, display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 30, fontWeight: 700, color: T.ink, letterSpacing: "-.022em" }}>{title}</div>
      <div style={{ fontSize: 16, color: T.ink2, lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}

/** A horizontal group inside a section. Sections must read WIDER THAN TALL, so
 *  many items go in ONE row rather than stacking into a column. Air Duct learned
 *  this on the live render: a second internal row is what makes a box tall. */
function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", gap: 34, alignItems: "flex-start" }}>{children}</div>;
}

/** The body of every section: caption on top, then one row of content. */
function Body({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 0, display: "flex", flexDirection: "column", gap: 26, alignItems: "flex-start" }}>
      {children}
    </div>
  );
}

const DEMO = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/everything-420/demo";

// A single document reading alone in a section gets a WIDE frame. At the 900px
// used for the multi-document row it would make its box taller than wide, which
// is the one shape the craft law forbids.
const SOLO_W = 2400;
const DOC_H = 1180;

/**
 * ONE node, ONE SectionGrid, six SectionBoxes, in the standard's order.
 *
 * This replaced three absolutely-positioned nodes whose coordinates were
 * hand-measured off the live board against the diagram's frame edges. That was
 * fragile by construction — a section growing by one card silently overlapped
 * the band beneath it, and the overlap was invisible until someone opened the
 * board. Flex-wrap cannot overlap. With the diagram gone there is nothing left
 * that needs absolute placement, so the whole measured-coordinate apparatus went
 * with it.
 */
function BoardNode() {
  return (
    <SectionGrid width={16400} gap={190}>
      {/* ── 0 · THE PROJECT ─────────────────────────────────────────────────
          Always first, always the project summary. It is simultaneously Erez's
          at-a-glance view and the first thing an agent reads to pick the thread
          back up — which is why it is one document and not a wall of cards. */}
      <SectionBox title="The project" layout="custom">
        <Body>
          <Caption
            title="Where this stands, and what is waiting on you"
            w={SOLO_W}
            body="The whole engagement in one document: who they are, the sentence it all turns on, what they already have, the first use case, what each side owes the other, the plan, and the four calls I made with my reasoning. The plan and the decisions used to be cards typed into this board file; they live here now, because a board presents documents and is never the home of prose."
          />
          <DocumentFrame slug="kb-doc/projects/everything-420" label="Everything 420 — the project" width={SOLO_W} height={DOC_H} />
        </Body>
      </SectionBox>

      {/* ── 1 · DISCOVERY ───────────────────────────────────────────────────
          Who they are, what they want, and their own documents. Everything in
          sections 2, 3 and 4 is DERIVED from these three — so they read first
          and they are the ones that have to stay true. */}
      <SectionBox title="Discovery" layout="custom">
        <Body>
          <Caption
            title="Who they are, what they said, and the document they handed us"
            w={2800}
            body="Read left to right. Who they actually are off their own storefront; the full read-back of both recorded calls, taken from the transcripts rather than from memory; and — the unusual one — a scope proposal written FOR them by a different provider, which Shuki passed to us. He did not engage her: it felt like she was casting a net. It is captured verbatim, and its callout warns not to read its tables as an inventory of what they have. It is validated demand, nothing more."
          />
          <Row>
            <DocumentFrame slug="kb-doc/consultancy/everything-420/the-business" label="1 · WHO — the business" width={900} height={DOC_H} />
            <DocumentFrame slug="kb-doc/consultancy/everything-420/discovery" label="2 · WHAT THEY SAID — both calls" width={900} height={DOC_H} />
            <DocumentFrame slug="kb-doc/consultancy/everything-420/current-stack" label="3 · THEIRS — a proposal they received (not ours, not built)" width={900} height={DOC_H} />
          </Row>
        </Body>
      </SectionBox>

      {/* ── 2 · SCOPE ───────────────────────────────────────────────────────
          NEW on 7 Aug. This engagement had no scoping attempt at all — it had a
          priced feature list, which is a different thing and comes later. Per
          skill://scoping the attempt comes BEFORE the questions: you design
          every phase as if you had to build it Monday, and the walls you hit are
          the only questions worth a client's attention. */}
      <SectionBox title="Scope" layout="custom">
        <Body>
          <Caption
            title="The attempt — made before their specification arrived, on purpose"
            w={SOLO_W}
            body="Their operation today against the same operation with our system in it; their eight pains in THEIR order and THEIR words, never re-ranked; then five root causes labelled as OUR analysis, because that is the step that shrinks the project. Fifteen builds mapped to those causes, and six questions that each state what they decide. A questionnaire tells them we have not started; a scope tells them here is what we think, correct us."
          />
          <DocumentFrame slug="kb-doc/consultancy/everything-420/scope" label="The scoping attempt — internal, not sent" width={SOLO_W} height={DOC_H} />
        </Body>
      </SectionBox>

      {/* ── 3 · COMMERCIAL ──────────────────────────────────────────────────
          DIVERGENCE, flagged: the sheet is a bespoke interactive component, not
          one of the three allowed section types. Kept deliberately — Erez asked
          for a live calculator rather than a link out to a spreadsheet, and the
          same exception is already carried on the Air Duct board.

          There is NO work order here and that is correct: their specification
          has not arrived, no feature list has been confirmed in writing, and no
          number has been ratified. The next commercial artifact is the
          scope-confirmation email, not the order. */}
      <SectionBox title="Commercial" layout="custom">
        <Body>
          <Caption
            title="What it would cost, and what has to happen before any of it goes out"
            w={2800}
            body="The written position on the left, the live sheet on the right, and they must agree — every figure in the document was computed off the sheet at its default knobs, so moving a knob makes the document stale. Fifteen features: six Shuki asked for by name, six from the proposal he was handed, three ours. Setup is one flat $5,000; phase 1 as agreed is $9,500; everything on the table is $41,800. No price has been discussed with them in any call."
          />
          <Row>
            <DocumentFrame slug="kb-doc/consultancy/everything-420/scope-and-price" label="Scope & price — internal, not sent" width={1100} height={1450} />
            <Everything420PricingSheet />
          </Row>
        </Body>
      </SectionBox>

      {/* ── 4 · THE SYSTEM ──────────────────────────────────────────────────
          Tailormind as it would be built for THIS business, with the CHANGELOG
          and the ATLAS REVIEWED TO line at the top of the page itself. This is
          where the removed diagram's content went, and where the divergences
          from the platform standard are argued — including the one worth Atlas's
          time: this is the first instance where the business database already
          exists and belongs to the CLIENT, which changes both the architecture
          and the price of the foundation. */}
      <SectionBox title="The system" layout="custom">
        <Body>
          <Caption
            title="The instance — and where it argues with the standard"
            w={SOLO_W}
            body="The connector drawn as a slot, which is the whole answer to a store migration happening underneath us. The data layer that distils and points rather than duplicating, which is the moment the technical buyer moved in the room. The knowledge that lives in none of their systems. And a control plane, because a clever automation you cannot see, permission or cap is not something you hand to an operations team. Section 07 lists six divergences from the platform standard and says which side should move."
          />
          <DocumentFrame slug="kb-doc/consultancy/everything-420/architecture" label="The system — CHANGELOG at the top · Atlas has not reviewed it" width={SOLO_W} height={DOC_H} />
        </Body>
      </SectionBox>

      {/* ── 5 · LIVE ────────────────────────────────────────────────────────
          DIVERGENCE, flagged: nothing here is running. For a prospect this shelf
          holds what we can SHOW. Three things, ONE internal row so the box stays
          wider than tall: the film that goes to them, the flow you click, and
          every screen at once for review. All three come off the same component,
          so they cannot drift from each other. */}
      <SectionBox title="Live" layout="custom">
        <Body>
          <Caption
            title="Nothing is deployed — this is what we can show, and it is one component behind all three"
            w={4600}
            body="The film is under two minutes and is only the application: one welcome screen, then the product. Erez cut every frame that was ABOUT it rather than it. The stepper beside it is the same flow to click, because the RHYTHM is the argument and a rhythm has to be felt — one image, wait; second, wait; third, wait; only after three clean approvals does the agent ask to run the other 37. Supervised, supervised, supervised, then trusted, which is Shuki's own sentence handed back to him working. Then all eight screens in order for review without clicking. Watch the third panel across screens 5 to 7: the design standard is a document in the data layer, and the designer's corrections write into it while you watch. Everyone claims their AI learns from feedback; almost nobody shows the file changing."
          />
          <Row>
            <PresentationFrame
              videoSrc={`${DEMO}/film.mp4`}
              poster={`${DEMO}/poster.jpg`}
              slidesBase={`${DEMO}/slides`}
              slideCount={9}
              pdf={`${DEMO}/everything-420-designer-demo.pdf`}
              label="Everything 420 · the Designer Agent · 1:50 · draft, not sent"
              width={1560}
            />
            <DesignerStepper />
            {/* ONE row of eight, not two rows of four. Eight across is very wide
                and short, which is exactly the shape the craft law wants; two
                rows of four would double this box's height for no gain. */}
            {DESIGNER_SCREENS.map((_, i) => <DesignerPlate key={i} step={i} />)}
          </Row>
        </Body>
      </SectionBox>
    </SectionGrid>
  );
}

const nodeTypes: NodeTypes = {
  e420board: BoardNode as unknown as NodeTypes[string],
};

function build(): { nodes: Node[]; edges: Edge[] } {
  return {
    nodes: [
      // ⚠️ INTERACTIVE NODES NEED THREE THINGS, not one. A nowheel/nodrag/nopan
      // class on the inner component is necessary but NOT sufficient: React Flow
      // gives a selectable:false wrapper `pointer-events: none`, and
      // `.react-flow__pane` sits at z-index 1 while `.react-flow__nodes` is auto
      // — so the pane paints OVER every node and eats the click. The component
      // renders perfectly, the state is perfectly correct, and nothing responds
      // to a mouse. Full recipe: the classes + stopPropagation on the component,
      // plus pointerEvents:'auto' and a zIndex above the pane HERE. This node
      // carries every clickable thing on the board — the scrolling documents,
      // the pricing sheet's inputs, the stepper, the film.
      {
        id: "board", type: "e420board", position: { x: 0, y: 0 },
        draggable: false, selectable: false,
        zIndex: 10, style: { pointerEvents: "auto" }, data: {},
      },
    ],
    edges: [],
  };
}

export function Everything420BoardPage() {
  const { nodes, edges } = useMemo(() => build(), []);
  return (
    <div style={{ position: "absolute", inset: 0 }} data-build={BUILD_SALT}>
      <BoardCanvas nodes={nodes} edges={edges} nodeTypes={nodeTypes}
        fitViewPadding={0.06} />
    </div>
  );
}
